<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { CheckOfflineService } from '@/services/CheckOfflineService'
import type { TodaysCheck } from '@/interfaces/TodaysCheck'
import { formatDayMonth, formatWeekday} from "@/utils"

const router = useRouter()
const checkOfflineService = CheckOfflineService()

const props = defineProps<{
  check: TodaysCheck
}>()

defineEmits(['buttonClick', 'onIssueReportSent'])

const onStartCheck = async (check: TodaysCheck) => {
  if (check.isCompleted && !check.canBeFinishedMultipleTimes) return;
  await checkOfflineService.createInProgressCheck(check)
  await router.push('/account/check')
}

const isCheckTypeExtra = (): boolean => {
  return props.check.category === 'Extra'
}

const isOverdue = (): boolean => {
  let currentDate = new Date()
  if (props.check.category != "Today") return false;
  return checkOfflineService.isOverdue(props.check);
}
function formatTime(time:string) {
  if (!time) return "";

  const [HH, mm] = time.split(":");
  const h = parseInt(HH, 10);

  if (h === 0) {
    return `12:${mm} AM`;
  }

  if (h === 12) {
    return `12:${mm} PM`;
  }

  if (h > 12) {
    return `${h - 12}:${mm} PM`;
  }

  return `${h}:${mm} AM`;
}
function getDue(check: TodaysCheck) {
  if (check.category == "This Month") {
    return "Due on " + formatDayMonth(check.nextDate) + " by " + removeSecondsAndConvertTo12Hour(check.due);
  }
  if (check.category == "This Week") {
    return "Due on " + formatWeekday(check.nextDate) + " by " + removeSecondsAndConvertTo12Hour(check.due);
  }
  return "Due " + check.days + " by " + removeSecondsAndConvertTo12Hour(check.due);
}

const removeSecondsAndConvertTo12Hour = (time:string) => {
  var t = time.split(':');
  return formatTime(time.split(':').slice(0, 2).join(':'));
}
</script>

<template>
  <div class="card" @click="onStartCheck($props.check)">
    <div
      :class="['card-sidebar']"
      :style="`background-color: ${check.icon.colour} !important`"
    >
      <div class="card-icon-container">
        <span v-if="check.icon.icon.length == 1" style="font-family: inter, arial">{{ check.icon.icon }}</span>
        <i :class="[check.icon.icon]" v-else></i>
      </div>
    </div>
    <div class="card-content">
      <el-text v-if="check.title" class="card-title" size="large">
        {{ check.title }}
      </el-text>
      <el-text v-if="!isOverdue() && !isCheckTypeExtra()" class="card-sub-title"
        >{{getDue(check)}}
      </el-text>
      <el-text
        v-if="check.description"
        class="card-description"
        line-clamp="3"
      >
        {{ check.description }}
      </el-text>

      <div
        v-if="
          isOverdue() &&
          !check.isCompleted
        "
        class="card-alert-container"
      >
        <img height="24" src="../assets/circle-exclamation-solid.svg" width="auto" />
        <el-text class="card-warning">Late! Was due {{ removeSecondsAndConvertTo12Hour(check.due) }}</el-text>
      </div>

      <div v-if="check.isCompleted" class="card-success-container">
        <img height="24" src="../assets/check-solid.svg" width="auto" />
        <el-text class="card-success">Completed</el-text>
      </div>

      <el-button
        v-if="
          check.isCompleted &&
          check.canBeFinishedMultipleTimes
        "
        class="action-button no-hover"
      >
        Start check
      </el-button>

      <el-button
        v-if="!check.isCompleted"
        class="action-button no-hover"
      >
        Start check
      </el-button>
    </div>
  </div>
</template>

<style scoped>
.card {
  display: flex;
  flex-direction: row;
  background: white;
  border-radius: 10px;
  overflow: clip;
  min-width: 320px;
  width: 100%;
  margin-bottom:10px;
}

.card-sidebar {
  background: #151740;
  min-width: 50px;
  min-height: 200px;
  display: flex;
  justify-content: center;
}

.card-sidebar .card-description {
  background: #289548;
}

.card-icon-container {
  background: white;
  margin-top: 10px;
  width: 30px;
  height: 30px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-icon-container i {
  color: #0f0f0f;
}

.card-title {
  margin-right: auto;
  font-weight: bold;
  color: #151740;
}

.card-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
}

.card-sub-title {
  margin-right: auto;
  font-weight: bold;
}

.card-description {
  margin-top: 10px;
  justify-content: left;
  width: 100%;
}

.card-alert-container {
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
}

.card-warning {
  text-align: center;
  margin-left: 5px;
  background: white;
  color: grey;
  font-weight: bold;
}

.card-success-container {
  margin-top: auto;
  display: flex;
  flex-direction: row;
}

.card-success {
  margin-left: 5px;
  background: white;
  color: grey;
  font-weight: bold;
}

.action-button {
  margin: auto 0 0;
  border-color: #289548;
  border-radius: 8px;
  height: 45px;
  font-weight: bold;
  font-size: 16px;
}

</style>
